import { memo, useMemo } from 'react';

import { CopyText, Flex, Heading, Label, Link, Text } from '@/components/core';
import { ActionRunTest, HttpUrl } from '@/components/flow';
import { InfoIcon } from '@/components/icons';
import { useGetAgentFromPath, useGetAppClientWithSecret } from '@/hooks';
import { useTriggerStore } from '@/store';
import { isNotConfiguredExport } from '@/utils';

export type ExportPowerAutomateConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportPowerAutomateConfiguration = ({}: ExportPowerAutomateConfigurationProps = {}) => {
  const action = useTriggerStore((state) => state.action);
  const { data: agent } = useGetAgentFromPath();

  const { data: appClient } = useGetAppClientWithSecret();
  const clientCredentials = useMemo(
    () => (appClient?.clientSecret ? btoa(`${appClient.clientId}:${appClient.clientSecret}`) : undefined),
    [appClient]
  );

  const isConfigured = !isNotConfiguredExport(action);

  return (
    <Flex col className="gap-md p-md grow">
      <Flex variant="plain" col className="p-md gap-4 rounded-lg border-1 border-gray-100 bg-white shadow-xs">
        <HttpUrl isDisabled placeholder="URL will be automatically updated from Power Automate" />
        <Flex col className="gap-xs">
          <Label>Client Credentials</Label>
          <CopyText isSensitive value={clientCredentials} />
        </Flex>
      </Flex>

      {isConfigured && <ActionRunTest label="Test connection" />}

      <div className="grow" />

      <Flex col className="group/tutorial-box mb-md rounded-lg border-1 border-gray-200 bg-gray-50 shadow-md">
        <Flex className="p-sm items-center">
          <Heading size="sm" className="gap-xs flex grow">
            <InfoIcon className="text-gray-400" />
            Setting up Power Automate
          </Heading>

          <Link href="https://docs.cradl.ai/integrations/powerautomate" className="font-regular text-sm">
            Read full guide
          </Link>
        </Flex>

        <Flex col className="p-md gap-sm rounded-lg border-1 border-gray-200 bg-white">
          <Text>
            1. Login to <Link href="https://make.powerautomate.com">Power Automate</Link>.
          </Text>
          <Text>
            2. Choose the <i>Extracted Data From Document</i> action as the trigger for your flow.
          </Text>
          <Text>
            3. If you don't have a Cradl connection in Power Automate already, setup a new <i>Connection</i> using the
            above <i>Client Credentials</i>.
          </Text>
          <Text>
            4. Use the <i>Export Action</i> dropdown and choose{' '}
            <i>
              {action?.name} from Agent "{agent?.name}"
            </i>
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const ExportPowerAutomateConfiguration = memo(
  IExportPowerAutomateConfiguration
) as typeof IExportPowerAutomateConfiguration;
